/**
 * All-boxes list page with search and grid view.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoxStore } from '../store/BoxContext';
import { BoxCard } from '../components/BoxCard';
import { SearchBar } from '../components/SearchBar';
import { EmptyState } from '../components/EmptyState';
import { BoxListSkeleton } from '../components/Skeleton';
import { EditModal } from '../components/EditModal';
import { useToast } from '../store/ToastContext';

type SortOption = 'createdAt' | 'name' | 'updatedAt' | 'location';

export function BoxListPage() {
  const navigate = useNavigate();
  const { state, fetchBoxes, createBox } = useBoxStore();
  const { addToast } = useToast();
  const [searchLocal, setSearchLocal] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newBoxName, setNewBoxName] = useState('');
  const [newBoxLocation, setNewBoxLocation] = useState('');
  const [newBoxNotes, setNewBoxNotes] = useState('');
  const [newBoxMarkerId, setNewBoxMarkerId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBoxes({ sortBy, sortOrder }).catch(() => {
      addToast('error', 'Failed to load boxes. Please try again.');
    });
  }, [fetchBoxes, sortBy, sortOrder, addToast]);

  function handleBoxClick(id: string) {
    navigate(`/boxes/${id}`);
  }

  function handleSearchChange(q: string) {
    setSearchLocal(q);
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
  }

  function handleLoadMore() {
    if (state.hasMore && !state.loading) {
      fetchBoxes({ page: state.page + 1, sortBy, sortOrder });
    }
  }

  async function handleCreateBox() {
    if (!newBoxName.trim() || !newBoxMarkerId.trim()) {
      addToast('error', 'Name and Marker ID are required');
      return;
    }

    setSaving(true);
    try {
      const box = await createBox({
        markerType: 'qr',
        markerId: newBoxMarkerId.trim(),
        name: newBoxName.trim(),
        location: newBoxLocation.trim() || undefined,
        notes: newBoxNotes.trim() || undefined,
      });
      setCreateModalOpen(false);
      setNewBoxName('');
      setNewBoxLocation('');
      setNewBoxNotes('');
      setNewBoxMarkerId('');
      addToast('success', 'Box created');
      navigate(`/boxes/${box.id}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create box';
      addToast('error', errorMsg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-text-primary">My Boxes</h1>
        <div className="flex items-center gap-2">
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => {
              const [sb, so] = e.target.value.split(':') as [SortOption, 'asc' | 'desc'];
              setSortBy(sb);
              setSortOrder(so);
            }}
            className="rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-text-secondary focus:border-accent focus:outline-none"
            aria-label="Sort boxes"
          >
            <option value="createdAt:desc">Newest first</option>
            <option value="createdAt:asc">Oldest first</option>
            <option value="name:asc">Name A-Z</option>
            <option value="name:desc">Name Z-A</option>
            <option value="location:asc">Location</option>
          </select>
          <button
            type="button"
            onClick={() => setCreateModalOpen(true)}
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover transition-colors flex items-center gap-1"
            aria-label="Create new box"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchBar value={searchLocal} onChange={handleSearchChange} />

      {/* Content */}
      {state.loading && state.boxes.length === 0 ? (
        <BoxListSkeleton />
      ) : state.error ? (
        <EmptyState
          title="Something went wrong"
          description={state.error}
          action={{ label: 'Try again', onClick: () => fetchBoxes({ sortBy, sortOrder }) }}
        />
      ) : state.boxes.length === 0 ? (
        <EmptyState
          icon={
            <svg
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          }
          title="No boxes yet"
          description="Scan a marker with the camera to create your first box, or click the 'New' button above to create one manually."
        />
      ) : (
        <>
          {/* Box grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {state.boxes.map((box) => (
              <BoxCard key={box.id} box={box} onClick={handleBoxClick} />
            ))}
          </div>

          {/* Load more */}
          {state.hasMore && (
            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={state.loading}
                className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary hover:bg-surface transition-colors disabled:opacity-50"
              >
                {state.loading ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      <EditModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create New Box"
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="new-marker-id"
              className="block text-sm font-medium text-text-secondary"
            >
              Marker ID <span className="text-danger">*</span>
            </label>
            <input
              id="new-marker-id"
              type="text"
              value={newBoxMarkerId}
              onChange={(e) => setNewBoxMarkerId(e.target.value)}
              placeholder="e.g. winter-clothes"
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <p className="mt-1 text-xs text-text-secondary">
              Unique identifier for this box (QR code will be generated)
            </p>
          </div>

          <div>
            <label htmlFor="new-name" className="block text-sm font-medium text-text-secondary">
              Name <span className="text-danger">*</span>
            </label>
            <input
              id="new-name"
              type="text"
              value={newBoxName}
              onChange={(e) => setNewBoxName(e.target.value)}
              placeholder="e.g. Winter Clothes"
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div>
            <label htmlFor="new-location" className="block text-sm font-medium text-text-secondary">
              Location
            </label>
            <input
              id="new-location"
              type="text"
              value={newBoxLocation}
              onChange={(e) => setNewBoxLocation(e.target.value)}
              placeholder="e.g. Garage, Basement"
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>

          <div>
            <label htmlFor="new-notes" className="block text-sm font-medium text-text-secondary">
              Notes
            </label>
            <textarea
              id="new-notes"
              value={newBoxNotes}
              onChange={(e) => setNewBoxNotes(e.target.value)}
              rows={3}
              placeholder="Additional details about this box"
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateModalOpen(false)}
              className="rounded-lg border border-border px-4 py-2 text-sm text-text-secondary hover:bg-surface transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateBox}
              disabled={saving || !newBoxName.trim() || !newBoxMarkerId.trim()}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </EditModal>
    </div>
  );
}
